/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { css } from '@emotion/react';

import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Text from '@mui/material/Typography';
import PublicHeader from '../components/PublicHeader';

import { useTranslation } from 'react-i18next';

import ProgressStepper from '../components/onboarding/ProgressStepper';
import StepperButtons from '../components/onboarding/StepperButtons';

import Step0 from '../components/onboarding/Step0';
import Step1 from '../components/onboarding/Step1';
import Step2 from '../components/onboarding/Step2';
import Step3Personal from '../components/onboarding/Step3Personal';
import Step3Company from '../components/onboarding/Step3Company';
import Step4 from '../components/onboarding/Step4';

import AppContext from '../contexts/app';

import {
  useUserQuery,
  useUserActions,
  updateUser,
} from '../actions/UserActions';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../utils/hooks';
import { getGeoInfo } from '../utils/geoip';
import { IS_SINGLE_TENENT_MODE } from '../constants';

function getTitle(t, step) {
  if (step === 0) {
    return t('Välj ditt språk');
  }
  if (step === 1) {
    return t('Hur kommer du använda Klang.ai?');
  }

  if (step === 2) {
    return t('Hur hittade du Klang.ai?');
  }

  if (step === 3) {
    return t('Berätta lite om dig');
  }

  if (step === 4) {
    return t('Några av våra betalningsalternativ');
  }

  return t('Ladda upp en fil och kom igång');
}

export default function Onboarding() {
  const { t } = useTranslation();
  const { user } = useUserQuery();
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const [step1, setStep1] = useState({
    usage: '',
    usageOtherText: '',
    interest: '',
    interestOtherText: '',
  });

  const [step2, setStep2] = useState({
    userType: '',
    userTypeOtherText: '',
    howFound: '',
    howFoundOtherText: '',
  });

  const [step3, setStep3] = useState({
    personalInfo: {
      telephoneNumber: '',
      name: '',
    },
    companyInfo: {
      role: '',
      fullName: '',
      telephoneNumber: '',
      employees: '',
      industry: '',
      name: '',
    },
    newsletter: true,
  });

  const isStudent = step2?.userType === 'student';
  const isResearcher = step2?.userType === 'researcher';

  const handleStep3Update = (data, type) => {
    if (data?.newsletter !== undefined) {
      setStep3((s) => ({ ...s, newsletter: data.newsletter }));
    } else if (type === 'company') {
      setStep3((s) => ({ ...s, companyInfo: { ...s.companyInfo, ...data } }));
    } else {
      setStep3((s) => ({ ...s, personalInfo: { ...s.personalInfo, ...data } }));
    }
  };

  const disabledNext = useMemo(() => {
    if (step === 0) {
      return false;
    }

    if (step === 1) {
      return (
        !step1.usage ||
        (step1.usage === 'other' && !step1.usageOtherText) ||
        !step1.interest ||
        (step1.interest === 'other' && !step1.interestOtherText)
      );
    }

    if (step === 2) {
      return (
        !step2.userType ||
        (step2.userType === 'other' && !step2.userTypeOtherText) ||
        !step2.howFound ||
        (step2.howFound === 'other' && !step2.howFoundOtherText)
      );
    }

    if (step === 3) {
      if (step2.userType === 'company') {
        return (
          !step3.companyInfo.role ||
          !step3.companyInfo.fullName ||
          !step3.companyInfo.telephoneNumber ||
          !step3.companyInfo.employees ||
          !step3.companyInfo.industry ||
          !step3.companyInfo.name
        );
      } else {
        return !step3.personalInfo.telephoneNumber || !step3.personalInfo.name;
      }
    }

    return false;
  }, [step, step1, step2, step3]);

  const { saveOnboardingAsync } = useUserActions();
  const { setUploadDialog } = useContext(AppContext);
  const { isMobile } = useIsMobile();

  useEffect(() => {
    if (IS_SINGLE_TENENT_MODE) {
      return;
    }

    getGeoInfo()
      .then((geo) => {
        return geo?.country;
      })
      .then((country) => {
        if (country) {
          return updateUser({ country });
        }
      })
      .catch((err) => {
        // Ignore
      });
  }, []);

  const handleStepChange = useCallback(
    async (s) => {
      if (s === 4) {
        const onboarding = {
          interest: step1.interest,
          interestOtherText: step1.interestOtherText,
          usage: step1.usage,
          usageOtherText: step1.usageOtherText,
          userType: step2.userType,
          howFound: step2.howFound,
          howFoundOtherText: step2.howFoundOtherText,
          companyInfo: step3.companyInfo,
          personalInfo: step3.personalInfo,
          skip: false,
          newsletter: step3.newsletter,
        };
        await saveOnboardingAsync(onboarding);
      } else if (s === 5) {
        setUploadDialog({ open: true });
        navigate('/');
        return;
      }
      setStep(s);
    },
    [step1, step2, step3, saveOnboardingAsync, navigate, setUploadDialog]
  );

  let title = getTitle(t, step);

  const goHome = useCallback(async () => {
    await saveOnboardingAsync({ skip: true });
    navigate('/');
  }, [navigate, saveOnboardingAsync]);

  return (
    <Stack
      css={css`
        background: var(--background-white);
        height: 100vh;
        overflow-x: hidden;
      `}
    >
      <PublicHeader />
      <Container
        sx={{ display: 'flex', flexDirection: 'column', py: 4, flexGrow: 2 }}
      >
        <Stack mb={3}>
          <Text variant="subtitle1" color="textSecondary">
            {t('Ett par frågor för att komma igång')}
          </Text>
          <Text variant="h6" fontWeight="700">
            {title}
          </Text>
        </Stack>
        <div
          css={css`
            display: flex;
            gap: 1.5rem;
            width: 100%;
            flex-grow: 2;
            flex-direction: ${isMobile ? 'column' : 'row'};
          `}
        >
          <ProgressStepper currentStep={step} />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 26px;
              width: 100%;
              flex-grow: 2;
            `}
          >
            <div
              css={css`
                flex-grow: 2;
              `}
            >
              {step === 0 && <Step0 user={user} />}

              {step === 1 && (
                <Step1
                  onUpdate={(data) => setStep1((s) => ({ ...s, ...data }))}
                  {...step1}
                />
              )}
              {step === 2 && (
                <Step2
                  onUpdate={(data) => setStep2((s) => ({ ...s, ...data }))}
                  {...step2}
                />
              )}

              {step === 3 && step2.userType === 'company' && (
                <Step3Company
                  {...step3.companyInfo}
                  newsletter={step3.newsletter}
                  onUpdate={(data) => handleStep3Update(data, 'company')}
                  isResearcher={isResearcher}
                />
              )}

              {step === 3 && step2.userType !== 'company' && (
                <Step3Personal
                  {...step3.personalInfo}
                  newsletter={step3.newsletter}
                  onUpdate={(data) => handleStep3Update(data, 'personal')}
                  isStudent={isStudent}
                />
              )}

              {step === 4 && <Step4 />}
            </div>
            <StepperButtons
              step={step}
              maxSteps={4}
              onStepChange={handleStepChange}
              disabled={disabledNext}
            />
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 1rem;

                @media (max-width: 700px) {
                  margin-top: 1rem;
                }
              `}
            >
              <Text color="textSecondary">
                {t('Vill du ställa in det här senare?')}
              </Text>
              <Text
                color="textSecondary"
                css={css`
                  cursor: pointer;
                  text-decoration: underline;
                `}
                onClick={goHome}
              >
                {t('Gå till systemet')}
              </Text>
            </div>
          </div>
        </div>
      </Container>
    </Stack>
  );
}
